import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, ChevronDown, Play } from "lucide-react";
import { noWidow } from "@/lib/utils";

/**
 * /sample — a layout showcase for the cosmetic-surgery audience: video hero,
 * Aesthetic-Insights-style editorial grids, and several dramatic sections.
 * All imagery is AI-generated placeholder art in /media/sample/; articles are
 * static placeholders (no DB).
 *
 * Four Veo hero-video candidates ship side by side: `?hero=1`..`?hero=4`
 * previews each without a redeploy, `HERO_DEFAULT` is what visitors get.
 */

const M = (f: string) => `/media/sample/${f}`;

const HERO_VIDEOS = [
  { id: 1, file: "sample-hero-v1-noir-web.mp4", label: "Noir" },
  { id: 2, file: "sample-hero-v2-gold-web.mp4", label: "Gold" },
  { id: 3, file: "sample-hero-v3-violet-web.mp4", label: "Violet" },
  { id: 4, file: "sample-hero-v4-ivory-web.mp4", label: "Ivory" },
] as const;
const HERO_DEFAULT = 1;

type SampleArticle = {
  title: string;
  excerpt: string;
  category: string;
  date: string;
  image: string;
  /** Tailwind aspect class for the card image. */
  aspect?: string;
};

const FEATURE: SampleArticle = {
  title: "The New Geometry of the Jawline",
  excerpt:
    "Structural contouring has moved from the operating room to the treatment chair. The industry's leading injectors on balance, proportion, and knowing when to stop.",
  category: "Facial Aesthetics",
  date: "August 2026",
  image: M("feature-east-asian-bob.jpg"),
};

const STORIES: SampleArticle[] = [
  {
    title: "Regenerative Aesthetics Comes of Age",
    excerpt: "Exosomes, polynucleotides, and the biology-first movement reshaping treatment plans.",
    category: "Science",
    date: "July 2026",
    image: M("portrait-black-afro.jpg"),
  },
  {
    title: "Contour Without the Knife",
    excerpt: "Energy-based body and face sculpting posts its strongest year on record.",
    category: "Devices",
    date: "July 2026",
    image: M("portrait-latina-wetlook.jpg"),
  },
  {
    title: "Glass Skin: Protocol or Philosophy?",
    excerpt: "Inside the multi-step clinical routines behind the industry's most requested finish.",
    category: "Skin",
    date: "June 2026",
    image: M("portrait-southasian-waves.jpg"),
  },
  {
    title: "The Quiet Rise of Preventative Botox",
    excerpt: "Patients in their twenties are rewriting the neurotoxin demand curve.",
    category: "Injectables",
    date: "June 2026",
    image: M("square-pixie-copper.jpg"),
  },
];

const MOSAIC: SampleArticle[] = [
  {
    title: "Designing the Natural Look",
    excerpt: "Why the best work is the work nobody notices.",
    category: "Trends",
    date: "May 2026",
    image: M("portrait-braids-teal.jpg"),
  },
  {
    title: "The Lip Flip Decade",
    excerpt: "Ten years of subtle-first lip aesthetics, by the numbers.",
    category: "Injectables",
    date: "May 2026",
    image: M("square-mideast-bangs.jpg"),
  },
  {
    title: "Skin Cycling, Clinically Speaking",
    excerpt: "Separating dermatology from the algorithm.",
    category: "Skin",
    date: "April 2026",
    image: M("feature-platinum-bun.jpg"),
  },
];

export const Route = createFileRoute("/sample")({
  validateSearch: (s: Record<string, unknown>) => ({
    hero: typeof s.hero === "string" || typeof s.hero === "number" ? Number(s.hero) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Sample — Medical Insight" },
      {
        name: "description",
        content: "A layout showcase: cinematic video hero and dramatic editorial layouts for the aesthetic industry.",
      },
    ],
  }),
  component: SamplePage,
});

function SamplePage() {
  const { hero } = Route.useSearch();
  const initial = HERO_VIDEOS.find((v) => v.id === hero)?.id ?? HERO_DEFAULT;
  const [heroId, setHeroId] = useState<number>(initial);
  const heroChoice = HERO_VIDEOS.find((v) => v.id === heroId) ?? HERO_VIDEOS[0];

  return (
    <div className="bg-background">
      <VideoHero video={heroChoice} onSelect={setHeroId} />
      <EditorialGrid />
      <FilmStrip />
      <QuoteBand />
      <ScrubCards />
      <MosaicSection />
      <ScrubSection />
      <ClosingCta />
    </div>
  );
}

/* ------------------------------------------------------------------ hero */

function VideoHero({
  video,
  onSelect,
}: {
  video: (typeof HERO_VIDEOS)[number];
  onSelect: (id: number) => void;
}) {
  return (
    <section className="relative min-h-[560px] h-[86svh] max-h-[900px] overflow-hidden bg-black">
      <video
        key={video.file}
        className="absolute inset-0 h-full w-full object-cover"
        src={M(video.file)}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      />
      {/* Legibility gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/30" />

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-4 pb-16 sm:pb-20">
        <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/70">
          Medical Insight — Sample
        </p>
        <h1 className="font-display mt-4 max-w-3xl text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-6xl">
          Beauty, engineered with intelligence.
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/80 sm:text-base">
          The research, the science, and the artistry behind modern medical
          aesthetics — told through the faces it transforms.
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <a
            href="#stories"
            className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black transition-transform hover:scale-[1.03]"
          >
            Explore the stories <ArrowRight className="h-4 w-4" />
          </a>
          <span className="inline-flex items-center gap-2 text-xs text-white/60">
            <Play className="h-3.5 w-3.5" /> Hero video: {video.label}
          </span>
        </div>
      </div>

      {/* Video-sample switcher */}
      <div className="absolute bottom-5 right-4 z-20 flex items-center gap-1.5 rounded-full border border-white/25 bg-black/35 p-1.5 backdrop-blur-md sm:right-6">
        {HERO_VIDEOS.map((v) => (
          <button
            key={v.id}
            type="button"
            aria-label={`Hero video ${v.id}: ${v.label}`}
            aria-pressed={v.id === video.id}
            onClick={() => onSelect(v.id)}
            className={`h-7 rounded-full px-2.5 text-[11px] font-medium transition-colors ${
              v.id === video.id
                ? "bg-white text-black"
                : "text-white/75 hover:bg-white/20 hover:text-white"
            }`}
          >
            {v.id} · {v.label}
          </button>
        ))}
      </div>

      <ChevronDown className="absolute bottom-4 left-1/2 z-10 h-5 w-5 -translate-x-1/2 animate-bounce text-white/60" />
    </section>
  );
}

/* -------------------------------------------------- editorial grid (news) */

function SectionHeading({ kicker, title }: { kicker: string; title: string }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3">
        <span className="brand-gradient h-px w-10" />
        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground">
          {kicker}
        </span>
      </div>
      <h2 className="font-display mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
        {title}
      </h2>
    </div>
  );
}

function EditorialGrid() {
  return (
    <section id="stories" className="mx-auto max-w-7xl px-4 py-16 sm:py-20">
      <SectionHeading kicker="Aesthetic Insights style" title="The face of the industry" />

      <div className="grid gap-6 lg:grid-cols-3 lg:gap-8">
        {/* Feature */}
        <a
          href="#stories"
          className="group overflow-hidden rounded-2xl border bg-card transition-all hover:-translate-y-1 hover:shadow-xl lg:col-span-2"
        >
          <div className="relative aspect-[16/9] overflow-hidden bg-secondary">
            <img
              src={FEATURE.image}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
            />
            <div className="absolute left-4 top-4 rounded-full bg-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-primary-foreground">
              Featured
            </div>
          </div>
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <span className="font-semibold text-primary">{FEATURE.category}</span>
              <span aria-hidden>•</span>
              <span>{FEATURE.date}</span>
            </div>
            <h3 className="font-display mt-3 text-2xl font-bold leading-tight tracking-tight transition-colors group-hover:text-primary sm:text-4xl">
              {FEATURE.title}
            </h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              {FEATURE.excerpt}
            </p>
            <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary">
              Read the story <span aria-hidden>→</span>
            </span>
          </div>
        </a>

        {/* Stacked compact cards */}
        <div className="flex flex-col gap-6">
          {STORIES.slice(0, 2).map((a) => (
            <CompactCard key={a.title} article={a} />
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        {STORIES.slice(2).map((a) => (
          <CompactCard key={a.title} article={a} wide />
        ))}
      </div>
    </section>
  );
}

function CompactCard({ article, wide }: { article: SampleArticle; wide?: boolean }) {
  return (
    <a
      href="#stories"
      className="group flex flex-1 gap-4 overflow-hidden rounded-xl border bg-card p-3 transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg sm:gap-5 sm:p-4"
    >
      <div
        className={`relative shrink-0 overflow-hidden rounded-lg bg-secondary ${
          wide ? "h-32 w-40 sm:h-36 sm:w-52" : "h-full min-h-28 w-32 sm:w-40"
        }`}
      >
        <img
          src={article.image}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
      </div>
      <div className="flex min-w-0 flex-col justify-center">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          <span className="font-semibold text-primary">{article.category}</span> · {article.date}
        </div>
        <h3 className="font-display mt-1 line-clamp-2 text-lg font-semibold leading-snug transition-colors group-hover:text-primary">
          {noWidow(article.title)}
        </h3>
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
          {article.excerpt}
        </p>
      </div>
    </a>
  );
}

/* ------------------------------------------------------- film strip scroll */

function FilmStrip() {
  const frames = [
    { image: M("portrait-black-afro.jpg"), caption: "Texture, celebrated" },
    { image: M("portrait-latina-wetlook.jpg"), caption: "Editorial polish" },
    { image: M("square-mideast-bangs.jpg"), caption: "Soft precision" },
    { image: M("portrait-southasian-waves.jpg"), caption: "Warmth in focus" },
    { image: M("portrait-braids-teal.jpg"), caption: "Sculpted light" },
    { image: M("square-pixie-copper.jpg"), caption: "Modern candor" },
  ];
  return (
    <section className="border-y bg-secondary/40 py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4">
        <SectionHeading kicker="Layout study 01" title="The film strip" />
        <p className="-mt-4 mb-8 max-w-xl text-sm text-muted-foreground">
          A horizontally scrolling gallery with snap points — drag or scroll sideways.
        </p>
      </div>
      <div className="flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-4 sm:px-8 [scrollbar-width:thin]">
        {frames.map((f, i) => (
          <figure
            key={f.caption}
            className="group relative w-64 shrink-0 snap-center overflow-hidden rounded-2xl sm:w-80"
          >
            <div className="aspect-[3/4] overflow-hidden bg-secondary">
              <img
                src={f.image}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
              />
            </div>
            <figcaption className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-black/70 to-transparent p-4 pt-12">
              <span className="font-display text-lg font-medium text-white">{f.caption}</span>
              <span className="font-mono text-xs text-white/60">
                {String(i + 1).padStart(2, "0")}
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- quote band */

function QuoteBand() {
  return (
    <section
      className="relative bg-cover bg-center bg-fixed py-24 sm:py-32"
      style={{ backgroundImage: `url(${M("feature-platinum-bun.jpg")})` }}
    >
      <div className="absolute inset-0 bg-black/65" />
      <div className="relative mx-auto max-w-4xl px-4 text-center">
        <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/60">
          Layout study 02 — the statement band
        </span>
        <blockquote className="font-display mt-6 text-3xl font-medium leading-tight text-white sm:text-5xl">
          “The best aesthetic outcome is the one that looks like it was
          <span className="brand-gradient-text"> always yours</span>.”
        </blockquote>
        <p className="mt-6 text-sm uppercase tracking-widest text-white/60">
          — The Aesthetic Industry Report
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------- scrubbing card archive */

const SCRUB_CARDS = [
  {
    video: "sample-scrub-noir.mp4",
    date: "May 12, 2026",
    title: "Toxin Competitors Target the Leaders",
    excerpt:
      "Cosmetic neurotoxins keep growing in 2026 even as the broader aesthetics market flattens — and the challengers are closing in.",
  },
  {
    video: "sample-scrub-gold.mp4",
    date: "April 29, 2026",
    title: "Injectables Report 26% YOY Growth",
    excerpt:
      "The latest AIA board session put digital acquisition at the center of the fastest-growing practices' playbooks.",
  },
  {
    video: "sample-scrub-ivory.mp4",
    date: "April 14, 2026",
    title: "The Regenerative Race Escalates",
    excerpt:
      "A market long dominated by three names is opening to a wider group of biology-first entrants — and patients are noticing.",
  },
] as const;

/**
 * Layout study 03 — an archive-style card row whose imagery is film, not
 * stills: as the row travels up the viewport, scroll progress scrubs all
 * three videos in a slight cascade (left leads, right trails).
 */
function ScrubCards() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const section = sectionRef.current;
        if (!section) return;
        const rect = section.getBoundingClientRect();
        const total = window.innerHeight + rect.height;
        const p = Math.min(1, Math.max(0, (window.innerHeight - rect.top) / total));
        videoRefs.current.forEach((v, i) => {
          if (!v || !Number.isFinite(v.duration) || v.duration <= 0) return;
          const staggered = Math.min(1, Math.max(0, p * 1.2 - i * 0.1));
          v.currentTime = staggered * (v.duration - 0.05);
        });
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section ref={sectionRef} className="mx-auto max-w-7xl px-4 py-16 sm:py-20">
      <SectionHeading kicker="Layout study 03" title="The living archive" />
      <p className="-mt-4 mb-8 max-w-xl text-sm text-muted-foreground">
        Article cards whose imagery is film — scrolling the page scrubs all three
        clips in a gentle cascade.
      </p>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {SCRUB_CARDS.map((card, i) => (
          <a
            key={card.title}
            href="#stories"
            className="group flex flex-col overflow-hidden rounded-xl border bg-card transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg"
          >
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-black sm:aspect-[4/4] lg:aspect-[4/5]">
              <video
                ref={(el) => {
                  videoRefs.current[i] = el;
                }}
                className="h-full w-full object-cover"
                src={M(card.video)}
                muted
                playsInline
                preload="auto"
              />
            </div>
            <div className="flex flex-1 flex-col p-5">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {card.date}
              </div>
              <h3 className="font-display mt-1 text-xl font-bold leading-snug transition-colors group-hover:text-primary">
                {card.title}
              </h3>
              <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                {card.excerpt}
              </p>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ mosaic grid */

function MosaicSection() {
  const [a, b, c] = MOSAIC;
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:py-20">
      <SectionHeading kicker="Layout study 04" title="The mosaic" />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2">
        <MosaicTile article={a} className="sm:col-span-2 lg:row-span-2" tall />
        <MosaicTile article={b} className="lg:col-span-2" />
        <MosaicTile article={c} className="lg:col-span-2" />
      </div>
    </section>
  );
}

function MosaicTile({
  article,
  className,
  tall,
}: {
  article: SampleArticle;
  className?: string;
  tall?: boolean;
}) {
  return (
    <a
      href="#stories"
      className={`group relative block overflow-hidden rounded-2xl ${className ?? ""}`}
    >
      <div className={`${tall ? "aspect-[3/4] lg:h-full lg:aspect-auto" : "aspect-[16/9]"} overflow-hidden bg-secondary`}>
        <img
          src={article.image}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-90 transition-opacity group-hover:opacity-100" />
      <div className="absolute inset-x-0 bottom-0 translate-y-2 p-5 transition-transform duration-500 group-hover:translate-y-0 sm:p-6">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-white/70">
          {article.category} · {article.date}
        </div>
        <h3 className="font-display mt-1 text-xl font-semibold leading-tight text-white sm:text-2xl">
          {article.title}
        </h3>
        <p className="mt-1 line-clamp-2 max-w-md text-xs text-white/75 opacity-0 transition-opacity duration-500 group-hover:opacity-100 sm:text-sm">
          {article.excerpt}
        </p>
      </div>
    </a>
  );
}

/* ------------------------------------------------------ scroll-scrub film */

/**
 * Layout study 05 — scrolling scrubs the film. A sticky band (not a full
 * screen) pins the video while scroll progress maps to playback time (the
 * scrub encode is keyframe-dense so seeking is smooth). A frosted-glass
 * message floats over the footage.
 */
function ScrubSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const section = sectionRef.current;
        const video = videoRef.current;
        if (!section || !video) return;
        const rect = section.getBoundingClientRect();
        const scrollable = rect.height - window.innerHeight;
        if (scrollable <= 0) return;
        const p = Math.min(1, Math.max(0, -rect.top / scrollable));
        setProgress(p);
        if (Number.isFinite(video.duration) && video.duration > 0) {
          video.currentTime = p * (video.duration - 0.05);
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative h-[220vh] bg-black">
      <div className="sticky top-[16vh] h-[56vh] min-h-[380px] overflow-hidden">
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          src={M("sample-scrub-violet.mp4")}
          muted
          playsInline
          preload="auto"
        />
        <div className="absolute inset-0 bg-black/25" />

        {/* Frosted-glass message */}
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div
            className="max-w-lg rounded-3xl border border-white/30 bg-white/15 p-6 text-center shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-8"
            style={{
              opacity: 0.35 + progress * 0.65,
              transform: `translateY(${(1 - progress) * 24}px)`,
            }}
          >
            <span className="text-[10px] font-semibold uppercase tracking-[0.35em] text-white/70">
              Layout study 05 — the scrub
            </span>
            <h2 className="font-display mt-3 text-2xl font-semibold leading-tight text-white sm:text-3xl">
              You direct the film.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/85">
              Scroll to move the camera. The page's scroll position drives the
              footage frame by frame — a canvas for launches, campaigns, and
              storytelling that rewards the reader's attention.
            </p>
            {/* Scrub progress */}
            <div className="mx-auto mt-6 h-1 w-40 overflow-hidden rounded-full bg-white/25">
              <div
                className="h-full rounded-full bg-white transition-[width] duration-75"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- final CTA */

function ClosingCta() {
  return (
    <section className="brand-gradient relative overflow-hidden py-20 text-center sm:py-24">
      <div className="relative mx-auto max-w-3xl px-4">
        <h2 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-5xl">
          This page is a sample.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/85 sm:text-base">
          Every layout above — the video hero, the editorial grids, the film
          strip, the statement band, the mosaic — can carry real articles,
          research, and campaigns from the admin.
        </p>
        <a
          href="/news"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black transition-transform hover:scale-[1.03]"
        >
          See the real Aesthetic Insights <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  );
}
